import {
  Plugin,
  Widget,
  viewToModelPositionOutsideModelElement,
  type ModelElement,
  type ViewDowncastWriter,
} from 'ckeditor5';
import compact from 'lodash/compact';
import FieldCommand from 'views/EMR/components/ckeditor-plugins/Field/command';
import {
  TemplateFieldDTO,
} from 'dtos';

export type TemplateField = TemplateFieldDTO & {
  selection: string[] | string;
};

export default class FieldEditing extends Plugin {
  static get requires() {
    return [Widget];
  }

  init() {
    this._defineSchema();
    this._defineConverters();

    this.editor.commands.add('field', new FieldCommand(this.editor));

    this.editor.editing.mapper.on(
      'viewToModelPosition',
      viewToModelPositionOutsideModelElement(this.editor.model, (viewElement) => viewElement.hasClass('__populate__-field')),
    );
  }

  _defineSchema() {
    const { schema } = this.editor.model;

    schema.register('field', {
      // Behaves like a self-contained inline object (e.g. an inline image)
      // allowed in places where $text is allowed (e.g. in paragraphs).
      // The inline widget can have the same attributes as text (for example linkHref, bold).
      inheritAllFrom: '$inlineObject',

      allowAttributes: [
        'value',
        'firstPosition',
        'lastPosition',
      ],
    });
  }

  _defineConverters() {
    const { conversion } = this.editor;

    // Helper method for both downcast converters.
    function createFieldView(modelItem: ModelElement, viewWriter: ViewDowncastWriter) {
      const value = modelItem.getAttribute('value') as TemplateField;
      const selection = typeof value?.selection === 'string'
        ? JSON.parse(value?.selection ?? '[]')
        : value?.selection ?? [];

      const name = compact([
        value?.title ?? '',
        selection.length > 0 ? `[${selection?.join(', ') ?? ''}]` : null,
      ]).join(' ');

      const fieldView = viewWriter.createContainerElement('span', {
        class: '__populate__-field',
        'data-field-id': value?.templateFieldId ?? 0,
        'data-field-title': value?.title ?? '--',
        'data-field-selection': JSON.stringify(selection),
      });

      const innerText = viewWriter.createText(name);
      viewWriter.insert(viewWriter.createPositionAt(fieldView, 0), innerText);
      return fieldView;
    }

    conversion.for('upcast').elementToElement({
      view: {
        name: 'span',
        classes: ['__populate__-field'],
      },
      model: (viewElement, { writer: modelWriter }) =>
        modelWriter.createElement('field', {
          value: {
            title: viewElement.getAttribute('data-field-title'),
            templateFieldId: viewElement.getAttribute('data-field-id'),
            options: viewElement.getAttribute('data-field-options'),
            selection: viewElement.getAttribute('data-field-selection'),
          },
        }),
    });

    conversion.for('editingDowncast').elementToElement({
      model: 'field',
      view: (modelItem, { writer: viewWriter }) => createFieldView(modelItem, viewWriter),
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'field',
      view: (modelItem, { writer: viewWriter }) => createFieldView(modelItem, viewWriter),
    });
  }
}
